// The side peek's address (design-system §3.12, spec 09 §4.15, D-314 rulings
// 2 and 4). Pure: no React and no router, so a screen's `validateSearch` can
// read `peek` without pulling a component in, and every rule for how opening,
// switching and closing write the address is unit-tested alone. The router
// half, `usePeekAddress` in `side-peek.tsx`, only carries these plans out.
//
// The address carries `peek=<kind>:<uuid>`. Opening from closed pushes a
// history entry and marks it in the entry's history state as pushed by the
// app. Switching to another record replaces the entry and keeps that mark.
// Closing goes back when the mark is there, and otherwise replaces the entry
// with one that has no `peek`: a link that arrived carrying a peek, or an entry
// another write replaced since (a view apply writes no history state, so the
// mark is gone and going back would undo that write), closes in place.

/** The four records a peek shows (spec 09 §4.15). */
export const PEEK_KINDS = ["customer", "quote", "order", "opportunity"] as const;
export type PeekKind = (typeof PEEK_KINDS)[number];

export interface PeekTarget {
  readonly kind: PeekKind;
  /** Lower-case uuid. */
  readonly id: string;
}

/** The panel's element id, so a Peek button can name it in `aria-controls`. */
export const SIDE_PEEK_ID = "ds-side-peek";

/** The history-state key that marks an entry the app pushed on opening. */
export const PEEK_PUSHED_STATE = "assesslyPeekPushed";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

function isKind(value: string): value is PeekKind {
  return (PEEK_KINDS as readonly string[]).includes(value);
}

/** Reads `<kind>:<uuid>`, and anything else as no peek. Never throws, because
 * a link can carry any text. */
export function parsePeek(raw: unknown): PeekTarget | undefined {
  if (typeof raw !== "string" || raw.length > 64) return undefined;
  const colon = raw.indexOf(":");
  if (colon <= 0) return undefined;
  const kind = raw.slice(0, colon);
  const id = raw.slice(colon + 1);
  if (!isKind(kind) || !UUID.test(id)) return undefined;
  return { kind, id: id.toLowerCase() };
}

/** The address form of a peek. */
export function formatPeek(target: PeekTarget): string {
  return `${target.kind}:${target.id.toLowerCase()}`;
}

/** The `validateSearch` half: the canonical code, or absent. */
export function peekSearchParam(raw: unknown): string | undefined {
  const target = parsePeek(raw);
  return target === undefined ? undefined : formatPeek(target);
}

/** A row's peek key, or none when its id is not a uuid. */
export function peekCode(kind: PeekKind, id: string | null | undefined): string | undefined {
  return typeof id === "string" && UUID.test(id) ? `${kind}:${id.toLowerCase()}` : undefined;
}

/** The search with `peek` set to `code`, or without it, and without any
 * member whose value is undefined, so the address never carries `peek=`. */
export function withPeek(
  search: Readonly<Record<string, unknown>>,
  code: string | undefined,
): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(search)) {
    if (key !== "peek" && value !== undefined) next[key] = value;
  }
  if (code !== undefined) next["peek"] = code;
  return next;
}

/** Whether a history entry's state says the app pushed it on opening. */
export function peekPushedInApp(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as Record<string, unknown>)[PEEK_PUSHED_STATE] === true
  );
}

/** What a peek write does to the address. `mark` says what happens to the
 * in-app mark: `set` writes it, `keep` carries the entry's state over and
 * `clear` writes none. */
export type PeekWrite =
  | { readonly kind: "none" }
  | { readonly kind: "back" }
  | {
      readonly kind: "navigate";
      readonly peek: string | undefined;
      readonly replace: boolean;
      readonly mark: "set" | "keep" | "clear";
    };

/** Opening `next` while `current` is open (or nothing is). */
export function planPeekOpen(current: string | undefined, next: string): PeekWrite {
  const code = peekSearchParam(next);
  if (code === undefined || code === current) return { kind: "none" };
  return current === undefined
    ? { kind: "navigate", peek: code, replace: false, mark: "set" }
    : { kind: "navigate", peek: code, replace: true, mark: "keep" };
}

/** Closing `current`, given the history state of the entry that holds it. */
export function planPeekClose(current: string | undefined, historyState: unknown): PeekWrite {
  if (current === undefined) return { kind: "none" };
  return peekPushedInApp(historyState)
    ? { kind: "back" }
    : { kind: "navigate", peek: undefined, replace: true, mark: "clear" };
}
